import { Button, Modal, Select, Surface } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PrintJob, PrintJobStatus } from '@warehouse/shared';
import { Download, RotateCw, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cancelJob, documentUrl, getPrintJob, listPrintJobs, listPrinters, reprintJob } from '../api';
import { PrintJobStatusBadge } from '../components/StatusBadges';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatDateTime } from '../../../lib/format';
import { listQueryKey } from '../../../lib/list-query';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

const STATUSES: PrintJobStatus[] = ['queued', 'sending', 'sent', 'failed', 'dead'];

/**
 * Print history + the dead-letter queue.
 *
 * Every job ever queued is here, with its attempts and its error, and every one
 * of them can be printed again — the old system persisted nothing, so a label
 * that failed was simply gone (docs/recon/printing-scanners.md A6 #2).
 */
export function PrintJobsPage(): ReactNode {
  const { t } = useTranslation(['devicesPrinting', 'common']);
  const queryClient = useQueryClient();
  const { state, setState, setFilter } = useListState({ defaultSort: 'createdAt', defaultDir: 'desc' });
  const [openJobId, setOpenJobId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['print-jobs', listQueryKey(state)],
    queryFn: () => listPrintJobs(state),
  });
  useLiveQuery(['print-jobs'], ['print-jobs']);

  const printers = useQuery({
    queryKey: ['printers', 'options'],
    queryFn: () => listPrinters({ page: 1, pageSize: 200 }),
  });

  const detail = useQuery({
    queryKey: ['print-job', openJobId],
    queryFn: () => getPrintJob(openJobId ?? ''),
    enabled: Boolean(openJobId),
  });

  const reprint = useMutation({
    mutationFn: (job: PrintJob) => reprintJob(job.id),
    onSuccess: () => {
      toastSuccess(t('devicesPrinting:jobs.toast.reprinted'));
      void queryClient.invalidateQueries({ queryKey: ['print-jobs'] });
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const cancel = useMutation({
    mutationFn: (job: PrintJob) => cancelJob(job.id),
    onSuccess: () => {
      toastSuccess(t('devicesPrinting:jobs.toast.cancelled'));
      void queryClient.invalidateQueries({ queryKey: ['print-jobs'] });
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const columns: DataTableColumn<PrintJob>[] = [
    {
      id: 'created',
      header: t('devicesPrinting:jobs.columns.created'),
      sortField: 'createdAt',
      cell: (row) => formatDateTime(row.createdAt),
    },
    {
      id: 'kind',
      header: t('devicesPrinting:jobs.columns.kind'),
      sortField: 'kind',
      cell: (row) => (
        <span className="font-mono text-xs">
          {t(`devicesPrinting:templates.kinds.${row.kind}`, { defaultValue: row.kind })}
        </span>
      ),
    },
    {
      id: 'printer',
      header: t('devicesPrinting:jobs.columns.printer'),
      cell: (row) => row.printerName ?? '—',
    },
    {
      id: 'copies',
      header: t('devicesPrinting:jobs.columns.copies'),
      align: 'right',
      hideBelow: 'md',
      cell: (row) => row.copies,
    },
    {
      id: 'status',
      header: t('devicesPrinting:jobs.columns.status'),
      sortField: 'status',
      cell: (row) => <PrintJobStatusBadge status={row.status} />,
    },
    {
      id: 'attempts',
      header: t('devicesPrinting:jobs.columns.attempts'),
      align: 'right',
      sortField: 'attempts',
      hideBelow: 'md',
      cell: (row) => row.attempts,
    },
    {
      id: 'error',
      header: t('devicesPrinting:jobs.columns.error'),
      hideBelow: 'lg',
      cell: (row) => (row.error ? <span className="font-mono text-xs text-red-600">{row.error}</span> : '—'),
    },
    {
      id: 'createdBy',
      header: t('devicesPrinting:jobs.columns.createdBy'),
      hideBelow: 'xl',
      cell: (row) => row.createdByName ?? '—',
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <div className="flex justify-end gap-1">
          {row.pdfFileId ? (
            <a
              href={documentUrl(row.id)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-brand-600 hover:underline"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <Download className="size-3.5" aria-hidden />
              {t('devicesPrinting:jobs.actions.downloadPdf')}
            </a>
          ) : null}
          {row.status === 'queued' || row.status === 'failed' ? (
            <Button
              size="sm"
              variant="ghost"
              icon={<X />}
              onClick={(event) => {
                event.stopPropagation();
                cancel.mutate(row);
              }}
            >
              {t('devicesPrinting:jobs.actions.cancel')}
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            icon={<RotateCw />}
            loading={reprint.isPending && reprint.variables?.id === row.id}
            onClick={(event) => {
              event.stopPropagation();
              reprint.mutate(row);
            }}
          >
            {t('devicesPrinting:jobs.actions.reprint')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('devicesPrinting:jobs.title')}
        subtitle={t('devicesPrinting:jobs.subtitle')}
      />

      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={query.data?.total ?? null}
        loading={query.isLoading}
        error={query.error}
        searchPlaceholder={t('devicesPrinting:jobs.searchPlaceholder')}
        emptyTitle={t('devicesPrinting:jobs.empty')}
        emptyDescription={t('devicesPrinting:jobs.emptyDescription')}
        onRowClick={(row) => {
          setOpenJobId(row.id);
        }}
        filters={
          <>
            <Select
              className="w-44"
              aria-label={t('devicesPrinting:jobs.filters.status')}
              value={String(state.filters?.['status'] ?? '')}
              options={[
                { value: '', label: t('devicesPrinting:jobs.filters.allStatuses') },
                ...STATUSES.map((status) => ({
                  value: status,
                  label: t(`devicesPrinting:jobs.status.${status}`),
                })),
              ]}
              onChange={(event) => {
                setFilter('status', event.target.value || undefined);
              }}
            />
            <Select
              className="w-56"
              aria-label={t('devicesPrinting:jobs.filters.printer')}
              value={String(state.filters?.['printerId'] ?? '')}
              options={[
                { value: '', label: t('devicesPrinting:jobs.filters.allPrinters') },
                ...(printers.data?.items ?? []).map((printer) => ({
                  value: printer.id,
                  label: printer.name,
                })),
              ]}
              onChange={(event) => {
                setFilter('printerId', event.target.value || undefined);
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilter('problems', state.filters?.['problems'] ? undefined : true);
              }}
            >
              {t('devicesPrinting:jobs.filters.onlyProblems')}
            </Button>
          </>
        }
      />

      {openJobId ? (
        <Modal
          open
          onClose={() => {
            setOpenJobId(null);
          }}
          title={t('devicesPrinting:jobs.detail.title')}
          width={720}
        >
          {detail.data ? (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <PrintJobStatusBadge status={detail.data.status} />
                <span className="text-ink-muted">{detail.data.printerName}</span>
                <span className="text-ink-muted">{formatDateTime(detail.data.createdAt)}</span>
              </div>
              {detail.data.status === 'dead' ? (
                <p className="text-sm text-amber-600">
                  {t('devicesPrinting:jobs.detail.deadHint', { attempts: detail.data.attempts })}
                </p>
              ) : null}
              <h3 className="font-mono text-xs uppercase tracking-[0.14em] text-ink-muted">
                {t('devicesPrinting:jobs.detail.zpl')}
              </h3>
              <Surface variant="subtle" className="max-h-96 overflow-auto p-3 scrollbar-thin">
                {detail.data.zpl ? (
                  <pre className="whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed text-ink">
                    {detail.data.zpl}
                  </pre>
                ) : (
                  <p className="text-sm text-ink-muted">{t('devicesPrinting:jobs.detail.noZpl')}</p>
                )}
              </Surface>
            </div>
          ) : (
            <p className="text-sm text-ink-muted">{t('common:common.loading')}</p>
          )}
        </Modal>
      ) : null}
    </>
  );
}
